import L from 'leaflet';

/* Oznake na ruti izabranog vozila: smer kretanja i mesta gde je stajalo.

   ── ZAŠTO OVO POSTOJI ─────────────────────────────────────────────────────
   Linija na mapi kaže KUDA je vozilo prošlo, ali ne i u kom smeru ni gde se
   zadržalo. Dispečeru je često baš to drugo najvažnije: gde je bila pauza,
   gde se čekalo na utovar, gde se stajalo na granici.

   ── STRELICE ──────────────────────────────────────────────────────────────
   Crtaju se kao obične Leaflet oznake sa zarotiranom SVG strelicom, a ne kroz
   dodatak `leaflet-polylinedecorator`: taj bi bio nov paket zbog jednog
   trougla, a rotacija po azimutu je pet linija računa.

   Razmak se bira po BROJU TAČAKA, ne po dužini u kilometrima — na uvećanoj
   mapi tačke su guste, na odzumljenoj retke, pa isti korak daje ujednačen
   izgled na svakom uvećanju bez merenja rastojanja.

   ── STAJANJA ──────────────────────────────────────────────────────────────
   Puna oznaka je pauza od 45 minuta ili duže (propisana pauza vozača),
   šuplja je kraće zadržavanje. Razlika je namerno u POPUNJENOSTI, a ne u
   boji ili veličini: obe su isti događaj iste važnosti, samo različitog
   trajanja, pa im oblik mora ostati isti da se čitaju kao jedna vrsta. */

/* Azimut od tačke A do tačke B, u stepenima od severa. Prava formula, ne
   `atan2` nad razlikom geografskih koordinata: na 45° širine jedan stepen
   dužine je upola kraći od stepena širine, pa bi strelice bez korekcije
   `cos(lat)` gledale ukoso. */
const azimut = ([lat1, lng1], [lat2, lng2]) => {
    const rad = (x) => (x * Math.PI) / 180;
    const dLng = rad(lng2 - lng1);
    const y = Math.sin(dLng) * Math.cos(rad(lat2));
    const x = Math.cos(rad(lat1)) * Math.sin(rad(lat2))
            - Math.sin(rad(lat1)) * Math.cos(rad(lat2)) * Math.cos(dLng);
    return (Math.atan2(y, x) * 180) / Math.PI;
};

/* Koliko tačaka preskočiti između dve strelice. Fiksan broj bi na kratkoj
   ruti dao jednu strelicu, a na dugačkoj stotine — zato se korak izvodi iz
   dužine niza, uz donju granicu da se strelice ne slepe. */
const KORAK_MIN = 12;
const STRELICA_NAJVISE = 60;

export const tackeStrelica = (segmenti) => {
    const svi = [];
    (segmenti || []).forEach(seg => {
        if (!seg || seg.length < 2) return;
        const korak = Math.max(KORAK_MIN, Math.ceil(seg.length / 8));
        for (let i = korak; i < seg.length - 1; i += korak) {
            svi.push({ tacka: seg[i], ugao: azimut(seg[i - 1], seg[i + 1]) });
        }
    });

    /* Gornja granica čuva mapu od zatrpavanja kad je ruta duga nekoliko dana:
       ravnomerno se prorede, umesto da se odseku poslednje (pa druga polovina
       rute ostane bez smera). */
    if (svi.length <= STRELICA_NAJVISE) return svi;
    const korak = Math.ceil(svi.length / STRELICA_NAJVISE);
    return svi.filter((_, i) => i % korak === 0);
};

export const IKONA_STRELICA = (ugao) => L.divIcon({
    className: 'gps-strelica-ikona',
    html: `<span class="gps-strelica" style="transform: rotate(${Math.round(ugao)}deg)">
             <svg viewBox="0 0 12 12" width="12" height="12" aria-hidden="true">
               <path d="M6 1.2 9.6 9 6 7.1 2.4 9z"/>
             </svg>
           </span>`,
    iconSize: [12, 12],
    iconAnchor: [6, 6],
    /* Strelice ne smeju da hvataju klik — inače se preko njih ne može ni
       kliknuti na rutu ni prevući mapa. */
    interactive: false,
});

/* Koliko je krug popunjen govori KOLIKO je stajanje trajalo.

   Ranije su postojala samo dva stanja — pun (45 min i vise) i suplj (sve
   ispod). To je odgovaralo na pitanje „je li bila propisana pauza", ali ne i
   na „koliko je stajao": zadrzavanje od sedam minuta i ono od cetrdeset
   izgledala su isto, iako je razlika izmedju njih pola radnog sata.

   Cetiri nivoa citaju se kao napunjenost case: sto je krug puniji, duze je
   stajao. Granice prate ono sto vozacu znaci — 15 minuta je kratak predah,
   30 je pauza, 45 je propisana pauza.

   Redosled u nizu je OPADAJUCI i na to se oslanja `find`: trazi se prvi prag
   koji je dostignut. Presloziti ga po rastucem redu tiho bi pokvarilo racun.
   Granice su ukljucive odozdo: tacno 45 minuta je pun krug. */
const NIVOI_STAJANJA = [
    { odMinuta: 45, klasa: 'gps-stajanje-pun' },
    { odMinuta: 30, klasa: 'gps-stajanje-pola' },
    { odMinuta: 15, klasa: 'gps-stajanje-cetvrt' },
];

export const nivoStajanja = (sekundi) => {
    const minuta = (Number(sekundi) || 0) / 60;
    const n = NIVOI_STAJANJA.find(x => minuta >= x.odMinuta);
    return n ? n.klasa : 'gps-stajanje-prazan';
};

/* Preko devet sati stajanje prestaje da bude pauza — to je DNEVNI ODMOR, i
   nije duža verzija istog događaja nego drugi događaj.

   Zato ne dobija peti nivo popunjenosti nego svoj znak: krug popunjen do kraja
   ne može da kaže „ovde je prespavao", a dispečeru je baš to podatak koji traži
   kad gleda gde je vozilo provelo noć. Krevet to kaže bez legende.

   Boja ostaje ista: menjati je značilo bi da je nešto u neredu, a dnevni odmor
   je propisan i očekivan. Razliku nosi znak, ne boja. */
const MINUTA_ODMOR = 9 * 60;

/* Crta se rucno, jer skup ikona koji aplikacija koristi nema krevet. Bez
   ispune i sa `currentColor`, pa nasledjuje boju sa omotaca kao i ostali
   znakovi na mapi. */
const IKONA_KREVET = `
<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"
     stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
  <path d="M3 7v11"/>
  <path d="M3 13h18v5"/>
  <path d="M3 18h18"/>
  <rect x="6.5" y="9.5" width="5" height="3.5" rx="1.2"/>
</svg>`;

export const ikonaStajanja = (sekundi, izabrano) => {
    const izabranoKlasa = izabrano ? ' gps-stajanje-izabrano' : '';

    if ((Number(sekundi) || 0) / 60 >= MINUTA_ODMOR) {
        return L.divIcon({
            className: 'gps-stajanje-ikona',
            html: `<span class="gps-odmor${izabranoKlasa}">${IKONA_KREVET}</span>`,
            /* Mera prati CSS: 26 px znaka + 2 px okvira sa svake strane + 1 px
               margine = 32. Kad se ovo razmimoidje sa CSS-om, znak prestane da
               stoji na svojoj tacki — pomeri se za razliku, i to na svakoj
               oznaci na mapi. */
            iconSize: [32, 32],
            iconAnchor: [16, 16],
        });
    }

    return L.divIcon({
        className: 'gps-stajanje-ikona',
        html: `<span class="gps-stajanje ${nivoStajanja(sekundi)}${izabranoKlasa}"></span>`,
        /* 18 px kruga + 3 px okvira sa svake strane + 1 px margine = 26.
           Ranije je ovde stajalo 16 dok je znak bio 22 px, pa je svaka oznaka
           stajanja bila pomerena tri piksela dole-desno od tacke na kojoj je
           vozilo stvarno stalo. */
        iconSize: [26, 26],
        iconAnchor: [13, 13],
    });
};

/* 5600 s -> „1h 33min". Sekunde se pišu samo kad su jedina mera — kod pauze
   od sat vremena broj sekundi ne znači ništa, a produžava red. */
export const trajanjeTeksta = (s) => {
    if (s == null) return '';
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sek = Math.round(s % 60);
    if (h) return `${h}h ${m}min`;
    if (m) return `${m}min ${sek} sec`;
    return `${sek} sec`;
};

/* Oznaka bez izgleda. Sluzi samo da popup ima o cemu da visi: Leaflet popup
   ne moze da stoji sam na koordinati. Prava oznaka bi na ruti bila jos jedna
   tacka koja nista ne znaci, pa zbunjuje. */
export const IKONA_NEVIDLJIVA = L.divIcon({
    className: 'gps-nevidljiva-ikona',
    html: '',
    iconSize: [1, 1],
    iconAnchor: [0, 0],
});

/* Broj predlozene trase, na samoj liniji. Bez njega se kartica u prozoru i
   linija na mapi ne mogu spojiti — dispecer vidi tri linije i tri kartice, a
   ne zna koja je koja. */
/* Trajanje u obliku „4h 55min". Stoji ovde, a ne u prozoru planiranja, jer ga
   trebaju oba — i kartica trase i oblacic na mapi — pa bi dve kopije pre ili
   kasnije pocele da se razlikuju. */
export const trajanje = (min) => {
    if (min == null) return '';
    const h = Math.floor(min / 60);
    return h ? `${h}h ${min % 60}min` : `${min}min`;
};

/* Kad se stize, ako se krene sada. Racuna se pri svakom iscrtavanju, pa se sam
   pomera kako vreme prolazi — mapa se ionako osvezava zajedno sa pozicijama. */
const stizanje = (min) => {
    if (min == null) return '';
    return new Date(Date.now() + min * 60000)
        .toLocaleTimeString('sr-RS', { hour: '2-digit', minute: '2-digit' });
};

/* Ide u innerHTML, pa se propusta kroz escape: vrednosti dolaze iz odgovora
   servisa, a takav sadrzaj se ne ugradjuje u markup neproveren. */
const bezbedno = (v) => String(v == null ? '' : v)
    .replace(/[&<>"]/g, c => MAPA_ESCAPE[c]);
const MAPA_ESCAPE = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' };

/* Oblacic na sredini trase.

   Ranije je ovde stajao samo redni broj. On govori KOJA je trasa, ali ne i
   zasto bi se bas ona izabrala — a bira se po vremenu. Kad je prozor sklonjen,
   broj nije znacio ni toliko, jer se kartica uz koju ide nije videla.

   Zato oblacic nosi trajanje, kilometre i vreme dolaska: to su podaci zbog
   kojih se u trase i gleda, pa se izbor sada moze napraviti sa same mape.
   Broj ostaje jer povezuje trasu sa karticom u prozoru. */
/* Odrediste plana — zastavica.

   Bez nje se na mapi videlo gde trase IDU, ali ne i gde se zavrsavaju: tri
   isprekidane linije koje se negde daleko spajaju ne kazu koja je tacka cilj,
   pogotovo kad je prozor sklonjen u traku.

   Kap je ista kao kod radova i blokada, da oznake na mapi govore istim
   jezikom, ali u boji akcenta i sa zastavicom: to je jedina oznaka koja ne
   javlja problem nego ODREDISTE, pa ne sme da lici na upozorenje. */
export const ikonaOdredista = () => L.divIcon({
    className: 'gps-cilj-ikona',
    html: '<span class="gps-cilj"><i class="pi pi-flag-fill"></i></span>',
    iconSize: [36, 36],
    /* Vrh kapi stoji NA tacki, ne u njenom sredistu — kap pokazuje nadole. */
    iconAnchor: [18, 34],
    popupAnchor: [0, -32],
});

export const ikonaTrase = (redni, izabrana, minuta, km) => {
    const vreme = trajanje(minuta);
    const stize = stizanje(minuta);
    const kilometri = km == null
        ? ''
        : `${km.toLocaleString('sr-RS', { maximumFractionDigits: 1 })} km`;

    const dno = [kilometri, stize && `⏱ ${stize}`].filter(Boolean).join('  ·  ');

    return L.divIcon({
        className: 'gps-trasa-ikona',
        html:
            `<span class="gps-trasa-oblacic${izabrana ? ' gps-trasa-oblacic-izabrana' : ''}">` +
                `<span class="gps-trasa-oblacic-vrh">` +
                    `<span class="gps-trasa-broj${izabrana ? ' gps-trasa-broj-izabrana' : ''}">${bezbedno(redni)}</span>` +
                    (vreme ? `<span class="gps-trasa-vreme">${bezbedno(vreme)}</span>` : '') +
                '</span>' +
                (dno ? `<span class="gps-trasa-dno">${bezbedno(dno)}</span>` : '') +
            '</span>',
        iconSize: [24, 24],
        iconAnchor: [12, 12],
    });
};
